import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  MaterialReactTable,
  useMaterialReactTable,
  createMRTColumnHelper,
} from 'material-react-table';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Modal from 'react-modal';
import { getReportVehicles } from '../../actions/apiGetInfo';
import { setReportVehicles } from '../../actions/apiSetInfo';
import { formatDaysHours, groupVehiclesByDepartments } from './chartUtils';
import ReportVehiclesPopup from './ReportVehiclesPopup';
import LoadingSpinner from '../../components/LoadingSpinner/loadingSpinner';
import { lookupWorkFlowPropertiesByWorkFlow } from '../../actions/lookups';
import _ from 'lodash';

// Helper function to convert hours to seconds (from ReportVehicles)
const hoursToSeconds = (hours) => {
  const [hourPart, minutePart = '0'] = hours.split(':').map(Number);
  return (hourPart * 3600) + (minutePart * 60);
};

const ReportDepartmentDetailsModal = ({ report_id: initialReportId, work_flow_id: initialWorkFlowId }) => {
  const dispatch = useDispatch();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [report_id, setReportId] = useState(initialReportId);
  const [work_flow_id, setWorkFlowId] = useState(initialWorkFlowId);
  const [loading, setLoading] = useState(false);
  const { report_vehicles, departments, dealer_settings, installation } = useSelector(state => ({
    report_vehicles: state.settings_reports.report_vehicles || [],
    departments: state.settings_departments.original_departments || [],
    dealer_settings: state.settings_dealerships.dealer_settings || [],
    installation: state.settings_dealerships.installation || {},
  }));

  const dealerSettingValue = (name) => {
    const settings = dealer_settings;
    let value = _.sortBy(_.filter(settings, o => o.key === name && o.dealership === installation.subdomain), 'id');
    if (value.length === 0) {
      value = _.sortBy(_.filter(settings, o => o.key === name && (o.dealership === '' || o.dealership === null || o.dealership === undefined)), 'id');
    }
    if (value.length > 0 && value[0]?.value) {
      // console.log(`ReportDepartmentDetailsModal: DealerSettingValue for ${name}: ${value[0].value}`);
      return value[0].value;
    }
    // console.warn(`ReportDepartmentDetailsModal: No ${name} found, using default`);
    return 'age-24_5';
  };

  const dealershipReportHours = dealerSettingValue('dealership_report_hours');

  useEffect(() => {
    // console.log('ReportDepartmentDetailsModal: Updating props:', { initialReportId, initialWorkFlowId });
    setReportId(initialReportId);
    setWorkFlowId(initialWorkFlowId);
  }, [initialReportId, initialWorkFlowId]);

  useEffect(() => {
    if (loading) {
      // console.log('ReportDepartmentDetailsModal: Loading vehicles:', { report_id, report_vehicles });
      if (report_vehicles) {
        // console.log('ReportDepartmentDetailsModal: Vehicles loaded:', { report_id, work_flow_id, report_vehicles });
        setLoading(false);
        setModalIsOpen(true);
      }
    }
  }, [report_vehicles, loading, report_id, work_flow_id]);

  const openModal = (newReportId, newWorkFlowId) => {
    if (newReportId > 0) {
      // console.log('ReportDepartmentDetailsModal: Opening modal with:', { newReportId, newWorkFlowId });
      setReportId(newReportId);
      setWorkFlowId(newWorkFlowId);
      setLoading(true);
      dispatch(getReportVehicles(newReportId)).then(response => {
        // console.log('ReportDepartmentDetailsModal: getReportVehicles response:', { reportId: newReportId, response });
        setLoading(false);
        setModalIsOpen(true);
      }).catch(error => {
        // console.error('ReportDepartmentDetailsModal: Error fetching vehicles:', { reportId: newReportId, error });
        setLoading(false);
        setModalIsOpen(true);
      });
    } else {
      // console.warn('ReportDepartmentDetailsModal: Invalid report_id:', newReportId);
      setModalIsOpen(false);
    }
  };

  const closeModal = () => {
    // console.log('ReportDepartmentDetailsModal: Closing modal');
    setModalIsOpen(false);
    setLoading(false);
    dispatch(setReportVehicles([]));
  };

  const departmentData = useMemo(() => {
    // Get the workflow properties for the current work_flow_id
    const workFlowProperties = dispatch(lookupWorkFlowPropertiesByWorkFlow(work_flow_id)) || [];
    const validDepartmentIds = workFlowProperties.map(prop => parseInt(prop.department_id)).filter(id => id);

    // Filter departments to only those in the workflow
    let data = groupVehiclesByDepartments(report_vehicles, departments, work_flow_id, null, dealershipReportHours);
    data = data.filter(item => validDepartmentIds.includes(parseInt(item.department_id)));
    data = data.sort((a, b) => {
      const deptA = departments.find(d => parseInt(d.id) === parseInt(a.department_id)) || { order: Infinity };
      const deptB = departments.find(d => parseInt(d.id) === parseInt(b.department_id)) || { order: Infinity };
      const orderA = deptA.order !== undefined ? deptA.order : Infinity;
      const orderB = deptB.order !== undefined ? deptB.order : Infinity;
      // console.log('ReportDepartmentDetailsModal: Sorting departments:', {
      //  deptA: { id: a.department_id, order: orderA },
      //  deptB: { id: b.department_id, order: orderB }
      // });
      return orderA - orderB;
    });
    // console.log('ReportDepartmentDetailsModal: Department data:', { report_id, work_flow_id, data, report_vehicles, departments });
    return data;
  }, [report_vehicles, departments, work_flow_id, dealershipReportHours]);

  const columnHelper = createMRTColumnHelper();

  const columns = [
    columnHelper.accessor('dealership', { 
      header: 'Dealership', 
      size: 120 
    }),
    columnHelper.accessor('name', { 
      header: 'Department', 
      size: 150 
    }),
    columnHelper.accessor('average_time', {
      header: 'Average Days',
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span>{formatDaysHours(row.original.average_time) || 'N/A'}</span>
        </Box>
      ),
      size: 100,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.average_time || 0;
        const b = rowB.original.average_time || 0;
        return a - b;
      },
      enableSorting: true,
      filterFn: (row, id, filterValue) => {
        // console.log('Average Days filterFn - filterValue:', filterValue, 'rowValue:', row.original[id]);
        if (!filterValue || !filterValue.value) return true;
        const rowValue = row.original[id] || 0; // Days
        const thresholdDays = hoursToSeconds(filterValue.value) / (24 * 3600); // Convert hours to days
        return filterValue.operator === 'gt' ? rowValue > thresholdDays : rowValue < thresholdDays;
      },
      Filter: ({ column }) => {
        const [operator, setOperator] = useState('gt');
        const [hours, setHours] = useState('');

        // console.log('Rendering Average Days filter:', { operator, hours });

        return (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, minWidth: '120px' }}>
            <FormControl sx={{ minWidth: 60 }}>
              <InputLabel>Op</InputLabel>
              <Select
                value={operator}
                onChange={(e) => {
                  setOperator(e.target.value);
                  column.setFilterValue({ operator: e.target.value, value: hours } || undefined);
                }}
                label="Op"
                size="small"
              >
                <MenuItem value="gt">gt</MenuItem>
                <MenuItem value="lt">lt</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Hours (e.g., 1:30)"
              value={hours}
              onChange={(e) => {
                setHours(e.target.value);
                column.setFilterValue({ operator, value: e.target.value } || undefined);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 80, maxWidth: 80 }}
              size="small"
            />
          </Box>
        );
      },
    }),
    columnHelper.accessor('vehicle_count', {
      header: 'Vehicle Count',
      size: 100,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.vehicle_count || 0;
        const b = rowB.original.vehicle_count || 0;
        // console.log('ReportDepartmentDetailsModal: Sorting vehicle_count:', { a, b });
        return a - b;
      },
      enableSorting: true,
      filterFn: (row, id, filterValue) => {
        // console.log('Vehicle Count filterFn - filterValue:', filterValue, 'rowValue:', row.original[id]);
        if (!filterValue || !filterValue.value) return true;
        const rowValue = row.original[id] || 0;
        const threshold = Number(filterValue.value) || 0;
        return filterValue.operator === 'gt' ? rowValue > threshold : rowValue < threshold;
      },
      Filter: ({ column }) => {
        const [operator, setOperator] = useState('gt');
        const [count, setCount] = useState('');

        // console.log('Rendering Vehicle Count filter:', { operator, count });

        return (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, minWidth: '120px' }}>
            <FormControl sx={{ minWidth: 60 }}>
              <InputLabel>Op</InputLabel>
              <Select
                value={operator}
                onChange={(e) => {
                  setOperator(e.target.value);
                  column.setFilterValue({ operator: e.target.value, value: count } || undefined);
                }}
                label="Op"
                size="small"
              >
                <MenuItem value="gt">gt</MenuItem>
                <MenuItem value="lt">lt</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Count"
              type="number"
              value={count}
              onChange={(e) => {
                setCount(e.target.value);
                column.setFilterValue({ operator, value: e.target.value } || undefined);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 80, maxWidth: 80 }}
              size="small"
            />
          </Box>
        );
      },
    }),
    columnHelper.display({
      id: 'details',
      header: 'Details',
      size: 100,
      Cell: ({ row }) => {
        const vehicleIds = report_vehicles
          .filter(vehicle => 
            vehicle.dealership === row.original.dealership &&
            (vehicle.completed_departments?.some(dept => parseInt(dept.department_id) === parseInt(row.original.department_id)) ||
             vehicle.completed_data_steps?.some(dept => parseInt(dept.department_id) === parseInt(row.original.department_id)))
          )
          .map(vehicle => vehicle.id);
        // console.log('ReportDepartmentDetailsModal: Vehicle IDs for row:', { dealership: row.original.dealership, department_id: row.original.department_id, vehicleIds });
        return (
          <ReportVehiclesPopup
            vehicle_ids={vehicleIds}
            department_id={row.original.department_id}
            dealership={row.original.dealership}
          />
        );
      },
    }),
  ];

  const now = new Date();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const year = now.getFullYear();
  const filename = `department_details_${month}_${day}_${year}`;

  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename,
  });

  const exportableColumnKeys = ['dealership', 'name', 'average_time', 'vehicle_count'];

  const handleExportRowsToCSV = (rows) => {
    // console.log('ReportDepartmentDetailsModal: Exporting rows to CSV:', { rowCount: rows.length });
    if (!rows || rows.length === 0) return;
    const rowData = rows.map(row => exportableColumnKeys.reduce((acc, key) => {
      acc[key] = key === 'average_time' ? formatDaysHours(row.original[key]) : row.original[key];
      return acc;
    }, {}));
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  };

  const handleExportDataToCSV = () => {
    // console.log('ReportDepartmentDetailsModal: Exporting all data to CSV:', { rowCount: departmentData.length });
    if (!departmentData || departmentData.length === 0) return;
    const rowData = departmentData.map(item => exportableColumnKeys.reduce((acc, key) => {
      acc[key] = key === 'average_time' ? formatDaysHours(item[key]) : item[key];
      return acc;
    }, {}));
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  };

  const handleExportRowsToPDF = (rows) => {
    // console.log('ReportDepartmentDetailsModal: Exporting rows to PDF:', { rowCount: rows.length });
    if (!rows || rows.length === 0) return;
    const doc = new jsPDF();
    const tableData = rows.map(row => exportableColumnKeys.map(key => 
      key === 'average_time' ? formatDaysHours(row.original[key]) : row.original[key]
    ));
    const tableHeaders = exportableColumnKeys.map(key => columns.find(col => col.accessorKey === key)?.header || 'Details');

    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
    });

    doc.save(filename);
  };

  const table = useMaterialReactTable({
    columns,
    data: departmentData,
    enableRowSelection: true,
    enablePagination: false,
    enableTopToolbar: true,
    enableColumnFilters: true,
    positionToolbarAlertBanner: 'bottom',
    initialState: { showColumnFilters: true }, // Show filters by default
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: 'none',
        backgroundColor: 'transparent',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 'bold',
        fontSize: '0.9rem',
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #ddd',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: '0.85rem',
        borderBottom: '1px solid #eee',
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          padding: '8px',
          flexWrap: 'wrap',
        }}
      >
        <Button
          disabled={table.getPrePaginationRowModel().rows.length === 0}
          onClick={() => handleExportRowsToCSV(table.getPrePaginationRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export to CSV
        </Button>
        <Button
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
          onClick={() => handleExportRowsToCSV(table.getSelectedRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export Selected Rows to CSV
        </Button>
        <Button
          disabled={table.getPrePaginationRowModel().rows.length === 0}
          onClick={() => handleExportRowsToPDF(table.getPrePaginationRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export to PDF
        </Button>
        <Button
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
          onClick={() => handleExportRowsToPDF(table.getSelectedRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export Selected Rows to PDF
        </Button>
      </Box>
    ),
  });

  return (
    <div className="col-md-3">
      <div className="btn-group">
        <button
          className="btn btn-large btn-secondary btn-block"
          onClick={() => {
            // console.log('ReportDepartmentDetailsModal: Button clicked:', { initialReportId, initialWorkFlowId });
            openModal(initialReportId, initialWorkFlowId);
          }}
          disabled={loading}
        >
          <i className="fal fa-building" />
        </button>
      </div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Department Details"
        className="customModal"
        ariaHideApp={false}
        overlayClassName="report-overlay"
      >
        <div className="customCard">
          <div className="customHeader">
            <button
              type="button"
              className="btn btn-danger btn-sm float-end space-left-2"
              aria-label="Close"
              onClick={closeModal}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div className="customContent">
            {departmentData.length === 0 ? (
              <div className="text-center" style={{ padding: '20px', fontSize: '1rem', color: '#666' }}>
                <LoadingSpinner loading />
              </div>
            ) : (
              <MaterialReactTable table={table} />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ReportDepartmentDetailsModal;